import type { GameEvent, GameState, PlayerInput, PlayerSide } from './core.js';

export const WAVEBEAN_SOCKET_PATH = '/ws/wavebean';
export const ROOM_CODE_LENGTH = 6;
export const ROOM_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

export type ClientMessage =
  | { type: 'create' }
  | { type: 'join'; roomCode: string; token?: string }
  | { type: 'input'; seq: number; input: PlayerInput }
  | { type: 'rematch' }
  | { type: 'leave' };

export type RoomStatus = 'waiting' | 'playing' | 'gameover';

export type ServerErrorCode =
  | 'INVALID_MESSAGE'
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'ROOM_EXPIRED'
  | 'RATE_LIMITED'
  | 'SERVER_FULL';

export type RoomClosedReason =
  | 'opponent-left'
  | 'opponent-timeout'
  | 'room-expired'
  | 'server-shutdown';

export type ServerMessage =
  | {
    type: 'room';
    roomCode: string;
    token: string;
    side: PlayerSide;
    status: RoomStatus;
    state: GameState;
    reconnected: boolean;
  }
  | { type: 'peer'; connected: boolean; reconnectDeadline?: number }
  | { type: 'state'; state: GameState; events: GameEvent[]; serverTime: number }
  | { type: 'rematch'; readySides: PlayerSide[] }
  | { type: 'closed'; reason: RoomClosedReason }
  | { type: 'error'; code: ServerErrorCode; message: string; retryable: boolean };

export function normalizeRoomCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, ROOM_CODE_LENGTH);
}

export function isRoomCode(value: unknown): value is string {
  return typeof value === 'string' && ROOM_CODE_PATTERN.test(value);
}

function isPlayerInput(value: unknown): value is PlayerInput {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<Record<keyof PlayerInput, unknown>>;
  return typeof candidate.left === 'boolean'
    && typeof candidate.right === 'boolean'
    && typeof candidate.jump === 'boolean'
    && typeof candidate.attack === 'boolean';
}

export function parseClientMessage(value: string): ClientMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) return null;
  const message = parsed as Record<string, unknown>;
  if (message.type === 'create' || message.type === 'rematch' || message.type === 'leave') {
    return { type: message.type };
  }
  if (message.type === 'join' && isRoomCode(message.roomCode)) {
    if (message.token !== undefined && typeof message.token !== 'string') return null;
    return {
      type: 'join',
      roomCode: message.roomCode,
      ...(message.token ? { token: message.token.slice(0, 128) } : {}),
    };
  }
  if (message.type === 'input'
    && Number.isSafeInteger(message.seq)
    && Number(message.seq) >= 0
    && isPlayerInput(message.input)) {
    return { type: 'input', seq: Number(message.seq), input: message.input };
  }
  return null;
}

function isPlayerSide(value: unknown): value is PlayerSide {
  return value === 0 || value === 1;
}

function isGameState(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<GameState>;
  return Number.isSafeInteger(state.tick)
    && Array.isArray(state.players)
    && state.players.length === 2
    && state.players.every((player) => Boolean(player)
      && typeof player.x === 'number'
      && typeof player.y === 'number'
      && typeof player.vx === 'number'
      && typeof player.vy === 'number')
    && Boolean(state.ball)
    && typeof state.ball?.x === 'number'
    && typeof state.ball?.y === 'number'
    && Array.isArray(state.scores)
    && state.scores.length === 2
    && state.scores.every((score) => Number.isSafeInteger(score))
    && (state.phase === 'ready' || state.phase === 'playing' || state.phase === 'point' || state.phase === 'gameover')
    && isPlayerSide(state.server)
    && (state.winner === null || isPlayerSide(state.winner));
}

export function parseServerMessage(value: string): ServerMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) return null;
  const message = parsed as Record<string, unknown>;
  if (message.type === 'room'
    && isRoomCode(message.roomCode)
    && typeof message.token === 'string'
    && isPlayerSide(message.side)
    && (message.status === 'waiting' || message.status === 'playing' || message.status === 'gameover')
    && isGameState(message.state)
    && typeof message.reconnected === 'boolean') {
    return message as ServerMessage;
  }
  if (message.type === 'peer' && typeof message.connected === 'boolean') {
    if (message.reconnectDeadline !== undefined && typeof message.reconnectDeadline !== 'number') return null;
    return message as ServerMessage;
  }
  if (message.type === 'state'
    && isGameState(message.state)
    && Array.isArray(message.events)
    && typeof message.serverTime === 'number') {
    return message as ServerMessage;
  }
  if (message.type === 'rematch'
    && Array.isArray(message.readySides)
    && message.readySides.every(isPlayerSide)) {
    return message as ServerMessage;
  }
  if (message.type === 'closed'
    && (message.reason === 'opponent-left'
      || message.reason === 'opponent-timeout'
      || message.reason === 'room-expired'
      || message.reason === 'server-shutdown')) {
    return message as ServerMessage;
  }
  if (message.type === 'error'
    && typeof message.code === 'string'
    && typeof message.message === 'string'
    && typeof message.retryable === 'boolean') {
    return message as ServerMessage;
  }
  return null;
}
